import { z } from "zod";

export const CONTENT_TYPES = ["NOTICE", "FAQ", "GUIDE"] as const;
export const CONTENT_STATUSES = ["DRAFT", "PUBLISHED", "ARCHIVED"] as const;

export type ContentType = (typeof CONTENT_TYPES)[number];
export type ContentStatus = (typeof CONTENT_STATUSES)[number];

const localDateTimePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;
const optionalLocalDateTime = z.string().refine(
  (value) => value === "" || (localDateTimePattern.test(value) && Number.isFinite(Date.parse(`${value}:00+09:00`))),
  "유효한 일시를 선택해 주세요.",
);

export const contentFormSchema = z.object({
  contentType: z.enum(CONTENT_TYPES),
  categoryCode: z.string().trim().max(40, "분류 코드는 40자 이하로 입력해 주세요."),
  title: z.string().trim().min(1, "제목을 입력해 주세요.").max(300, "제목은 300자 이하로 입력해 주세요."),
  content: z.string().trim().min(1, "본문을 입력해 주세요."),
  pinned: z.boolean(),
  displayStartedAt: optionalLocalDateTime,
  displayEndedAt: optionalLocalDateTime,
  attachmentFileAssetIds: z.array(z.number().int().positive()).refine(
    (ids) => new Set(ids).size === ids.length,
    "같은 첨부파일을 중복해서 등록할 수 없습니다.",
  ),
}).superRefine((values, context) => {
  if (values.displayStartedAt && values.displayEndedAt && values.displayStartedAt >= values.displayEndedAt) {
    context.addIssue({ code: "custom", path: ["displayEndedAt"], message: "표시 종료일시는 시작일시보다 늦어야 합니다." });
  }
});

export type ContentFormValues = z.infer<typeof contentFormSchema>;

export function parseContentType(value: string | null): ContentType {
  return CONTENT_TYPES.includes(value as ContentType) ? value as ContentType : "NOTICE";
}

export function parseContentStatus(value: string | null): ContentStatus | "" {
  return CONTENT_STATUSES.includes(value as ContentStatus) ? value as ContentStatus : "";
}

export function toUtcLocalDateTime(kstLocalDateTime: string): string | null {
  return kstLocalDateTime ? new Date(`${kstLocalDateTime}:00+09:00`).toISOString().slice(0, 19) : null;
}

export function toKstLocalDateTime(utcLocalDateTime?: string | null): string {
  if (!utcLocalDateTime) return "";
  const explicitUtc = /(?:Z|[+-]\d{2}:\d{2})$/.test(utcLocalDateTime) ? utcLocalDateTime : `${utcLocalDateTime}Z`;
  const date = new Date(explicitUtc);
  if (!Number.isFinite(date.getTime())) return "";
  return new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date).replace(" ", "T");
}
